import React, { useCallback, useMemo } from "react";
import {
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  DateRangePicker,
  DateRangePickerProps,
  FormField,
  Grid,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
} from "@cloudscape-design/components";
import { RealmSelector } from "../common";
import { FailuresConfig, toStatuses } from "../../hooks/useStepFunctionFailures";
import { StateMachineSummary } from "../../services/StepFunctionFailuresService";
import { DateRangeConverter } from "../../utils/DateRangeConverter";
import { Stage, SFN_FAILURES_LABELS } from "../../constants";

interface StepFunctionFailuresFormProps {
  config: FailuresConfig;
  stage: Stage;
  loading: boolean;
  stateMachines: StateMachineSummary[];
  stateMachinesStatus: "loading" | "error" | "finished";
  onConfigChange: (config: FailuresConfig) => void;
  onReloadStateMachines: () => void;
  onSubmit: () => void;
}

const GRID_MAIN = [{ colspan: 8 }, { colspan: 4 }] as const;

const RELATIVE_OPTIONS: DateRangePickerProps.RelativeOption[] = [
  { key: "last-1-hour", amount: 1, unit: "hour", type: "relative" },
  { key: "last-6-hours", amount: 6, unit: "hour", type: "relative" },
  { key: "last-1-day", amount: 1, unit: "day", type: "relative" },
  { key: "last-3-days", amount: 3, unit: "day", type: "relative" },
  { key: "last-1-week", amount: 1, unit: "week", type: "relative" },
];

const formatUnit = (unit: DateRangePickerProps.TimeUnit, value: number): string => (value === 1 ? unit : `${unit}s`);

const DATE_RANGE_I18N: DateRangePickerProps.I18nStrings = {
  relativeModeTitle: "Relative range",
  absoluteModeTitle: "Absolute range",
  relativeRangeSelectionHeading: "Choose a range",
  customRelativeRangeOptionLabel: "Custom range",
  customRelativeRangeOptionDescription: "Set a custom range in the past",
  customRelativeRangeDurationLabel: "Duration",
  customRelativeRangeDurationPlaceholder: "Enter duration",
  customRelativeRangeUnitLabel: "Unit of time",
  startDateLabel: "Start date",
  endDateLabel: "End date",
  startTimeLabel: "Start time",
  endTimeLabel: "End time",
  dateTimeConstraintText: "Use 24 hour format.",
  clearButtonLabel: "Clear and dismiss",
  cancelButtonLabel: "Cancel",
  applyButtonLabel: "Apply",
  todayAriaLabel: "Today",
  nextMonthAriaLabel: "Next month",
  previousMonthAriaLabel: "Previous month",
  formatRelativeRange: (range) => `Last ${range.amount} ${formatUnit(range.unit, range.amount)}`,
  formatUnit,
};

const validateRange = (value: DateRangePickerProps.Value | null): DateRangePickerProps.ValidationResult => {
  if (!value) {
    return { valid: false, errorMessage: SFN_FAILURES_LABELS.RANGE_INCOMPLETE };
  }
  if (value.type === "absolute" && (!value.startDate || !value.endDate)) {
    return { valid: false, errorMessage: SFN_FAILURES_LABELS.RANGE_INCOMPLETE };
  }
  if (!DateRangeConverter.toDates(value)) {
    const errorMessage =
      value.type === "absolute" ? SFN_FAILURES_LABELS.RANGE_START_BEFORE_END : SFN_FAILURES_LABELS.RANGE_DURATION_POSITIVE;
    return { valid: false, errorMessage };
  }
  return { valid: true };
};

export const StepFunctionFailuresForm: React.FC<StepFunctionFailuresFormProps> = ({
  config,
  stage,
  loading,
  stateMachines,
  stateMachinesStatus,
  onConfigChange,
  onReloadStateMachines,
  onSubmit,
}) => {
  const update = useCallback((partial: Partial<FailuresConfig>): void => onConfigChange({ ...config, ...partial }), [
    config,
    onConfigChange,
  ]);

  const options = useMemo<SelectProps.Option[]>(() => stateMachines.map((machine) => ({ value: machine.arn, label: machine.name })), [
    stateMachines,
  ]);

  const selectedOption = useMemo(() => options.find((option) => option.value === config.stateMachineArn) || null, [
    options,
    config.stateMachineArn,
  ]);

  const isDisabled = useMemo(
    () => !config.stateMachineArn || !DateRangeConverter.toDates(config.dateRange) || toStatuses(config).length === 0,
    [config]
  );

  return (
    <Container header={<Header variant="h2">{SFN_FAILURES_LABELS.CONFIGURATION}</Header>}>
      <SpaceBetween size="m">
        <Grid gridDefinition={[...GRID_MAIN]}>
          <FormField label={SFN_FAILURES_LABELS.STATE_MACHINE}>
            <Select
              selectedOption={selectedOption}
              options={options}
              placeholder={SFN_FAILURES_LABELS.STATE_MACHINE_PLACEHOLDER}
              filteringType="auto"
              statusType={stateMachinesStatus}
              loadingText={SFN_FAILURES_LABELS.STATE_MACHINES_LOADING}
              errorText={SFN_FAILURES_LABELS.STATE_MACHINES_ERROR}
              recoveryText={SFN_FAILURES_LABELS.STATE_MACHINES_RECOVERY}
              empty={SFN_FAILURES_LABELS.STATE_MACHINES_EMPTY}
              onLoadItems={onReloadStateMachines}
              onChange={(e) => update({ stateMachineArn: e.detail.selectedOption.value || "" })}
            />
          </FormField>
          <FormField label={SFN_FAILURES_LABELS.REALM}>
            <RealmSelector value={config.realm} stage={stage} onChange={(realm) => update({ realm, stateMachineArn: "" })} />
          </FormField>
        </Grid>

        <FormField label={SFN_FAILURES_LABELS.TIMEFRAME} stretch>
          <DateRangePicker
            value={config.dateRange}
            onChange={(e) => update({ dateRange: e.detail.value })}
            relativeOptions={RELATIVE_OPTIONS}
            isValidRange={validateRange}
            i18nStrings={DATE_RANGE_I18N}
            placeholder={SFN_FAILURES_LABELS.TIMEFRAME_PLACEHOLDER}
          />
        </FormField>

        <FormField label={SFN_FAILURES_LABELS.STATUSES_LABEL}>
          <ColumnLayout columns={3}>
            <Checkbox checked={config.includeFailed} onChange={(e) => update({ includeFailed: e.detail.checked })}>
              {SFN_FAILURES_LABELS.INCLUDE_FAILED}
            </Checkbox>
            <Checkbox checked={config.includeTimedOut} onChange={(e) => update({ includeTimedOut: e.detail.checked })}>
              {SFN_FAILURES_LABELS.INCLUDE_TIMED_OUT}
            </Checkbox>
            <Checkbox checked={config.includeAborted} onChange={(e) => update({ includeAborted: e.detail.checked })}>
              {SFN_FAILURES_LABELS.INCLUDE_ABORTED}
            </Checkbox>
          </ColumnLayout>
        </FormField>

        <Button variant="primary" onClick={onSubmit} loading={loading} disabled={isDisabled}>
          {SFN_FAILURES_LABELS.SEARCH}
        </Button>
      </SpaceBetween>
    </Container>
  );
};
